import { useRef, useState } from "react"
import { Button, HStack, IconButton, NativeSelectField, NativeSelectRoot, Spinner, Stack } from "@chakra-ui/react"
import { FaArrowUp, FaDownload } from "react-icons/fa"
import { LuRefreshCw } from "react-icons/lu"
import { useQueryClient } from "@tanstack/react-query"
import { Section } from "@/components"
import { useIrrDeals, useIrrDashboard } from "@/hooks"
import { downloadNewCompaniesXlsx, recomputeIrrMetrics } from "@/api/irr"
import { toaster } from "@/components/ui/toaster"
import { IrrDealsComponent, IrrDashboardTab, IrrPortfolioTab, IrrQuarterlyReportTab } from "./components"

function getQuarterOptions() {
  const now = new Date();
  let q = Math.floor(now.getMonth() / 3);
  let y = now.getFullYear();
  const opts: { label: string; quarter: number; year: number }[] = [];
  for (let i = 0; i < 4; i++) {
    opts.push({ label: `Q${q + 1} ${y}`, quarter: q + 1, year: y });
    if (--q < 0) { q = 3; y--; }
  }
  return opts;
}
const QUARTER_OPTIONS = getQuarterOptions();

const TABS = ["Dashboard", "Portfolio", "Deals", "Quarterly"]

export const IrrComponent = () => {
  const { data: irrDealFunds, isLoading: isDealsLoading } = useIrrDeals()
  const { data: fundDashboard, isLoading: isDashboardLoading } = useIrrDashboard()

  const [activeTab, setActiveTab] = useState("Dashboard")
  const [isDownloading, setIsDownloading] = useState(false)
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0])
  const [isRecomputing, setIsRecomputing] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const scrollToTop = () => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleRecompute = async () => {
    setIsRecomputing(true)
    try {
      const { deals, companies } = await recomputeIrrMetrics()
      await queryClient.invalidateQueries({ queryKey: ["irrCompanies"] })
      await queryClient.invalidateQueries({ queryKey: ["irrDeals"] })
      await queryClient.invalidateQueries({ queryKey: ["irrDashboard"] })
      toaster.create({ title: `Metrics refreshed — ${deals} deals, ${companies} company records updated`, type: "success" })
    } catch {
      toaster.create({ title: "Failed to refresh metrics", type: "error" })
    } finally {
      setIsRecomputing(false)
    }
  }

  const handleDownloadXlsx = async () => {
    setIsDownloading(true)
    try {
      await downloadNewCompaniesXlsx(selectedQuarter.quarter, selectedQuarter.year)
      toaster.create({ title: "Excel file downloaded", type: "success" })
    } catch {
      toaster.create({ title: "Failed to download Excel file", type: "error" })
    } finally {
      setIsDownloading(false)
    }
  }

  const isInitialLoading = isDealsLoading && isDashboardLoading

  if (isInitialLoading) {
    return (
      <Section h="100vh" alignItems="center" justifyContent="center">
        <Spinner size="lg" />
      </Section>
    )
  }

  return (
    <Section ref={sectionRef} overflowY="auto" h="100vh">
      <Stack gap={4}>
        {/* Top bar */}
        <HStack gap={3} flexWrap="wrap">
          {/* Tab pills */}
          {TABS.map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? "solid" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </Button>
          ))}

          {/* Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecompute}
            loading={isRecomputing}
            ml="auto"
          >
            <LuRefreshCw />
            Refresh Metrics
          </Button>
          <NativeSelectRoot size="sm" variant="outline" w="auto" minW="120px">
            <NativeSelectField
              value={`${selectedQuarter.year}-Q${selectedQuarter.quarter}`}
              onChange={(e) => {
                const opt = QUARTER_OPTIONS.find(o => `${o.year}-Q${o.quarter}` === e.target.value);
                if (opt) setSelectedQuarter(opt);
              }}
            >
              {QUARTER_OPTIONS.map(opt => (
                <option key={`${opt.year}-Q${opt.quarter}`} value={`${opt.year}-Q${opt.quarter}`}>
                  {opt.label}
                </option>
              ))}
            </NativeSelectField>
          </NativeSelectRoot>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadXlsx}
            loading={isDownloading}
          >
            <FaDownload />
            New Companies Excel
          </Button>
        </HStack>

        {activeTab === "Dashboard" && (
          <IrrDashboardTab
            funds={fundDashboard ?? []}
            isLoading={isDashboardLoading}
          />
        )}
        {activeTab === "Portfolio" && (
          <IrrPortfolioTab
            irrDealFunds={irrDealFunds ?? []}
            isLoading={isDealsLoading}
          />
        )}
        {activeTab === "Deals" && (
          <IrrDealsComponent
            isLoading={isDealsLoading}
            irrDealFunds={irrDealFunds ?? []}
          />
        )}
        {activeTab === "Quarterly" && (
          <IrrQuarterlyReportTab
            funds={fundDashboard ?? []}
            irrDealFunds={irrDealFunds ?? []}
            quarter={selectedQuarter.quarter}
            year={selectedQuarter.year}
            isLoading={isDealsLoading || isDashboardLoading}
          />
        )}
      </Stack>

      <IconButton
        aria-label="Scroll to top"
        size="lg"
        borderRadius={10}
        boxShadow="lg"
        bg="fab.bg"
        onClick={scrollToTop}
        position="fixed"
        bottom={6}
        right={6}
      >
        <FaArrowUp color="fab.icon" />
      </IconButton>
    </Section>
  )
}
